import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { clientApi } from '@/services/api'
import type { Client } from '@/types'
import { BookOpen, BarChart3, FileText, ArrowRight, LogOut } from 'lucide-react'

export default function ClientDashboard() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    clientApi.list().then((res) => {
      setClients(res.data.clients || [])
    }).finally(() => setLoading(false))
  }, [])

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <div className="bg-white border-b border-surface-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#0078C8] rounded-lg flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <span className="text-[13px] font-semibold text-[#000A1E]">Client Portal</span>
        </div>
        <div className="flex items-center gap-4">
          {user && (
            <span className="text-xs text-[rgba(0,10,30,0.75)]">{user.full_name || user.email}</span>
          )}
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-xs text-[rgba(0,10,30,0.75)] hover:text-red-600 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-[21px] font-bold text-[#000A1E] leading-[32px]">Welcome back</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">Access your financial reports and statements below.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 border-2 border-[#0078C8] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : clients.length === 0 ? (
          <div className="card p-12 text-center">
            <div className="w-14 h-14 bg-[#0078C8]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <BookOpen className="w-7 h-7 text-[#0078C8]" />
            </div>
            <p className="text-[13px] font-medium text-[#000A1E] mb-1">No clients yet</p>
            <p className="text-xs text-[rgba(0,10,30,0.75)]">Your accountant hasn't linked any clients to your account.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((client) => (
              <div
                key={client.id}
                className="card p-5 hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => navigate(`/portal/${client.id}/reports`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#0078C8]/10 rounded-xl flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-[#0078C8]" />
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-[#000A1E]">{client.name}</p>
                      <p className="text-xs text-[rgba(0,10,30,0.75)] mt-0.5">{client.currency} · {client.entity_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[#0078C8] opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs font-medium">View Reports</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-surface-100">
                  <div className="flex items-center gap-1.5 text-xs text-[rgba(0,10,30,0.75)]">
                    <FileText className="w-3.5 h-3.5" />
                    <span>Profit & Loss</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[rgba(0,10,30,0.75)]">
                    <FileText className="w-3.5 h-3.5" />
                    <span>Balance Sheet</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[rgba(0,10,30,0.75)]">
                    <FileText className="w-3.5 h-3.5" />
                    <span>Trial Balance</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
